import {
  ApplicationConfig,
  provideZoneChangeDetection,
  Injectable,
} from '@angular/core';
import {
  GuardsCheckEnd,
  NavigationEnd,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withComponentInputBinding,
  PreloadingStrategy,
  Route,
} from '@angular/router';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { ConfigService } from './config/config.service';
import { CONFIG_LOADER } from './config/config-loader.token';
import { provideIcons } from './providers/icon.provider';
import { NotificationService } from './notificaction/notification.service';
import { AuthService } from './auth/services/auth.service';
import { GlobalStore } from './store/app.store';
import { medusaInterceptor } from './auth/interceptors/medusa.interceptor';
import { authBaseInterceptor } from './auth/interceptors/auth-base.interceptor';
import { provideNgProgressRouter } from 'ngx-progressbar/router';
import {
  HTTP_OPTION_MAILISEARCH_CTX,
  HTTP_OPTION_MAILISEARCH_CTX_CONFIG,
  HTTP_OPTION_TOKEN_CTX,
  HTTP_OPTION_TOKEN_CTX_CONFIG,
} from './tokens';

import { provideToastr } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { provideServiceWorker } from '@angular/service-worker';
import { backendNetworkInterceptor } from './interceptors/backend-network.interceptor';
import { BACKEND_URL_MAP } from './tokens/backend-url.token';
import { backendTimingInterceptor } from './interceptors/backend-timing.interceptor';

@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload']) {
      return load();
    }
    return of(null);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    // ✅ CAMBIO: se eliminó provideAnimations() duplicado. Con
    // provideAnimationsAsync() alcanza; tenerlos ambos anula el propósito
    // del "async" (carga el motor de animaciones eager de todas formas).
    provideAnimationsAsync(),

    provideZoneChangeDetection({
      eventCoalescing: true,
      runCoalescing: true,
    }),

    provideClientHydration(withEventReplay(), withIncrementalHydration()),

    provideHttpClient(
      withFetch(),
      withInterceptors([
        backendNetworkInterceptor,
        backendTimingInterceptor,
        medusaInterceptor,
        authBaseInterceptor,
      ])
    ),

    provideIcons(),

    provideRouter(
      routes,
      withPreloading(SelectivePreloadingStrategy),
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'disabled',
      }),
      withComponentInputBinding()
    ),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: HTTP_OPTION_TOKEN_CTX_CONFIG,
      useValue: { context: HTTP_OPTION_TOKEN_CTX },
    },
    {
      provide: HTTP_OPTION_MAILISEARCH_CTX_CONFIG,
      useValue: { context: HTTP_OPTION_MAILISEARCH_CTX },
    },

    // ✅ NUEVO: implementación de CONFIG_LOADER para el navegador.
    // Mismo comportamiento que tenía ConfigService antes (HTTP relativo a
    // la base de la app). Esta es la rama que SÍ debe seguir siendo HTTP,
    // porque en el cliente no hay filesystem.
    {
      provide: CONFIG_LOADER,
      useFactory: (http: HttpClient) => (fileName: string) =>
        http.get(`./assets/config/${fileName}.json`),
      deps: [HttpClient],
    },
    {
      provide: BACKEND_URL_MAP,
      useValue: [], // navegador: nunca reescribe, siempre URL pública
    },

    AuthService,
    ConfigService,
    GlobalStore,
    NotificationService,

    provideNgProgressRouter({
      startEvents: [GuardsCheckEnd],
      completeEvents: [NavigationEnd],
      minDuration: 500,
    }),
    provideToastr(),

    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
